/**
 * Utility functions for handling search requests and pagination
 */

/**
 * Process a search request event, ensuring pagination parameters are properly set
 * @param {Object} detail - The event detail object
 * @param {Object} options - Additional options
 */
export const processSearchRequest = (detail, options = {}) => {
  // Create a new detail object to avoid mutating the original
  const processedDetail = { ...detail };
  
  // Ensure we have the proper pagination parameters
  if (!processedDetail.page) {
    processedDetail.page = 1;
  }
  
  // Ensure we have the proper products per page, defaulting to settings or 24 if not provided
  if (!processedDetail.productsPerPage) {
    processedDetail.productsPerPage = options.productsPerPage || 24;
  }
  
  // Calculate pagination offsets
  const skip = (processedDetail.page - 1) * processedDetail.productsPerPage;
  const limit = processedDetail.productsPerPage;
  
  return {
    ...processedDetail,
    pagination: {
      page: processedDetail.page,
      skip,
      limit,
      productsPerPage: processedDetail.productsPerPage
    }
  };
};

/**
 * Update the URL with pagination parameters
 * @param {number} page - The current page number
 * @param {Object} options - Additional options
 */
export const updateUrlWithPaginationParams = (page, options = {}) => {
  if (typeof window === 'undefined') return;
  
  const url = new URL(window.location);
  
  // If page is 1 and we're clearing params, remove the page parameter
  if (page === 1 && options.clearParams) {
    url.searchParams.delete('page');
  } else if (page > 1) {
    // Otherwise, set the page parameter if it's greater than 1
    url.searchParams.set('page', page);
  }
  
  // Update URL without triggering a page reload
  window.history.replaceState({}, '', url);
};

/**
 * Get the current page from the URL
 * @returns {number} The current page number from the URL or 1 if not present
 */
export const getCurrentPageFromUrl = () => {
  if (typeof window === 'undefined') return 1;
  
  const urlParams = new URLSearchParams(window.location.search);
  const page = urlParams.get('page');
  
  return page ? parseInt(page, 10) : 1;
};

/**
 * Save the current scroll position to session storage
 * Useful when navigating away from the collection page to a product page
 */
export const saveScrollPosition = () => {
  if (typeof window === 'undefined') return;
  
  sessionStorage.setItem('collectionScrollPos', window.scrollY.toString());
};

/**
 * Restore the saved scroll position, if available
 */
export const restoreScrollPosition = () => {
  if (typeof window === 'undefined') return;
  
  setTimeout(() => {
    const scrollPos = sessionStorage.getItem('collectionScrollPos');
    if (scrollPos) {
      window.scrollTo(0, parseInt(scrollPos, 10));
      sessionStorage.removeItem('collectionScrollPos');
    }
  }, 100);
};